import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  type CountryCode,
} from 'libphonenumber-js';

export interface Country {
  code: CountryCode;
  /** Localised display name (Intl.DisplayNames), falls back to the ISO code. */
  name: string;
  dialCode: string;
  flag: string;
}

/** ISO code → flag using regional indicator symbols (no image assets). */
export function flagFor(code: string): string {
  return code
    .toUpperCase()
    .replace(/./g, (ch) => String.fromCodePoint(0x1f1e6 - 65 + ch.charCodeAt(0)));
}

const nameCache = new Map<string, Intl.DisplayNames | null>();
function displayNames(locale: string): Intl.DisplayNames | null {
  if (!nameCache.has(locale)) {
    try {
      nameCache.set(locale, new Intl.DisplayNames([locale], { type: 'region' }));
    } catch {
      nameCache.set(locale, null);
    }
  }
  return nameCache.get(locale) ?? null;
}

/** All countries libphonenumber knows, localised and sorted for the given UI locale. */
export function listCountries(locale: string): Country[] {
  const names = displayNames(locale);
  const collator = new Intl.Collator(locale);
  return getCountries()
    .map((code) => ({
      code,
      name: names?.of(code) ?? code,
      dialCode: `+${getCountryCallingCode(code)}`,
      flag: flagFor(code),
    }))
    .sort((a, b) => collator.compare(a.name, b.name));
}

/** Countries surfaced first in the picker (GCC + common). */
export const PRIORITY_COUNTRIES: CountryCode[] = [
  'QA',
  'SA',
  'AE',
  'KW',
  'BH',
  'OM',
  'EG',
  'JO',
  'GB',
  'US',
];

/** Common IANA time zones → country, used when the browser locale has no region. */
const TIMEZONE_COUNTRY: Record<string, CountryCode> = {
  'Asia/Qatar': 'QA',
  'Asia/Riyadh': 'SA',
  'Asia/Dubai': 'AE',
  'Asia/Kuwait': 'KW',
  'Asia/Bahrain': 'BH',
  'Asia/Muscat': 'OM',
  'Asia/Amman': 'JO',
  'Asia/Beirut': 'LB',
  'Asia/Baghdad': 'IQ',
  'Asia/Damascus': 'SY',
  'Africa/Cairo': 'EG',
  'Africa/Casablanca': 'MA',
  'Africa/Tunis': 'TN',
  'Africa/Algiers': 'DZ',
  'Africa/Khartoum': 'SD',
  'Europe/Istanbul': 'TR',
  'Europe/London': 'GB',
  'Europe/Dublin': 'IE',
  'Europe/Paris': 'FR',
  'Europe/Berlin': 'DE',
  'Europe/Madrid': 'ES',
  'Europe/Rome': 'IT',
  'Europe/Amsterdam': 'NL',
  'America/New_York': 'US',
  'America/Chicago': 'US',
  'America/Denver': 'US',
  'America/Los_Angeles': 'US',
  'America/Toronto': 'CA',
  'Asia/Karachi': 'PK',
  'Asia/Kolkata': 'IN',
  'Asia/Dhaka': 'BD',
  'Asia/Manila': 'PH',
  'Asia/Jakarta': 'ID',
  'Asia/Kuala_Lumpur': 'MY',
  'Asia/Singapore': 'SG',
  'Australia/Sydney': 'AU',
};

const known = new Set<string>(getCountries());

/**
 * Best-effort country guess from the browser: locale region first, then time zone.
 * The server hint (GET /auth/locale, cf.country in production) overrides this when present.
 */
export function detectCountry(): CountryCode | undefined {
  const locales = [navigator.language, ...(navigator.languages ?? [])];
  for (const tag of locales) {
    try {
      const region = new Intl.Locale(tag).region;
      if (region && known.has(region)) return region as CountryCode;
    } catch {
      // ignore malformed tags
    }
  }
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const fromTz = TIMEZONE_COUNTRY[tz];
    if (fromTz) return fromTz;
  } catch {
    // ignore
  }
  return undefined;
}

export function isKnownCountry(code: string | undefined | null): code is CountryCode {
  return Boolean(code) && known.has(String(code).toUpperCase());
}

/** Format a national number as the user types (digits only in, pretty string out). */
export function formatAsYouType(input: string, country: CountryCode): string {
  const formatter = new AsYouType(country);
  return formatter.input(input);
}

/** Validate a national or international number for the chosen country. */
export function phoneStatus(
  input: string,
  country: CountryCode,
): { valid: boolean; e164?: string; tooShort: boolean } {
  const digits = input.replace(/[^\d+]/g, '');
  if (digits.replace(/\D/g, '').length < 4) return { valid: false, tooShort: true };
  const parsed = parsePhoneNumberFromString(input, country);
  if (parsed?.isValid()) return { valid: true, e164: parsed.number, tooShort: false };
  // Also accept the number typed with its own country code but no plus.
  const intl = parsePhoneNumberFromString(`+${digits.replace(/\D/g, '')}`);
  if (intl?.isValid()) return { valid: true, e164: intl.number, tooShort: false };
  return { valid: false, tooShort: false };
}
